/**
 * @file SearchInventory.js
 * @version 2.37.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-13
 * @modified 2026-11-02
 * @description Search and view inventory items synced from Xero (+ transport unit weight).
 *              The add/edit form it used to carry inline now lives in
 *              InventoryItemFormModal, so the order screen can create an item
 *              without sending the user away from a half-built order.
 *
 *              Sales and Purchase account are filtered from the filter row
 *              only — searchable multi-selects with ticks (Cam, 2026-10-16) —
 *              and no longer from a right-click on their headers.
 *
 *              2.33.0 (2026-10-20): the item window's Cost Price History is
 *              CostPriceHistorySection — the Xero cost and the till's (Epos
 *              Now) cost and sale price as their own series.
 *
 *              2.34.0 (2026-10-28, Cam — O1): the item window shows the item's
 *              product group (ProductGroupItemRow — a link that opens it on
 *              the Product Groups tab, its variant / additive, Change group…).
 *
 *              2.35.0 (2026-10-29, Cam — N8): the product group on the table.
 *              A Product group column (the group's name — a link that opens it
 *              on the Product Groups tab — with the variant / additive under
 *              it, faded for an archived group, "—" for none), sortable; a
 *              Product group filter in the filter row (live groups and "No
 *              group"); the column's right-click filter; the search box reaches
 *              the group's name and variant. Before migration product_groups.sql
 *              (or with no groups yet) the column shows "—" and the filter
 *              says why. Words in utils/productGroups.js.
 *
 *              2.37.0 (2026-11-02, Cam — "we'd also want the supplier field
 *              added as a column to Manage Inventory component table please"):
 *              a Supplier column after Product Group — the supplier's name, a
 *              supplier group's "<parent> group" with "Any depot" under it, an
 *              archived contact said, "—" for none — sortable; a Supplier
 *              filter in the filter row (No supplier, each group, each supplier
 *              an item names); the column's right-click filter; the search box
 *              reaches the supplier's name and number and a group's depots; the
 *              export carries it. The item window's Supplier row
 *              (ItemSupplierRow). Before migration item_supplier.sql the column
 *              shows "—" and the filter says why. Words in utils/itemSupplier.js.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { API_URL } from '../config';
import ReactDOM from 'react-dom';
import './SearchLayout.css';
import './ResizableColumns.css';
import './SearchInventory.css';
import Dropdown from './Dropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import Input from './Input';
import PaginationControls from './PaginationControls';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import LoadingBar from './LoadingBar';
import AuditHistoryModal from './AuditHistoryModal';
import InventoryItemFormModal from './InventoryItemFormModal';
import CostPriceHistorySection from './CostPriceHistorySection';
import CalculatedCostPanel from './CalculatedCostPanel';
import ItemUnitsRow from './ItemUnitsRow';
import ProductGroupItemRow from './ProductGroupItemRow';
import ItemSupplierRow from './ItemSupplierRow';
import useItemSupplierOptions from '../hooks/useItemSupplierOptions';
import {
  supplierFilterBlock, supplierFilterOptions, summarizeSupplierFilter, supplierColumnLabel, supplierText
} from '../utils/itemSupplier';
import {
  OPEN_GROUP_KEY, groupFilterBlock, groupFilterOptions, summarizeGroupFilter,
  groupColumnLabel, groupCellText, variantLabel
} from '../utils/productGroups';
import MineralPricingModal from './MineralPricingModal';
import RowActionsMenu from './RowActionsMenu';
import CrossSearchDialog from './CrossSearchDialog';
import { runCrossSearch } from '../utils/crossSearch';
import TableContextMenu from './TableContextMenu';
import { useSlideOutButton } from './SlideOutButton';
import { getTaxTypeLabel } from '../utils/taxTypes';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import { ACCOUNT_NAMES } from '../utils/accountCodes';
import { purchaseSideAccounts, purchaseSideText } from '../utils/itemAccounts';
import LastXeroSync from './LastXeroSync';
import ItemSourceIcon, { ITEM_SOURCES } from './ItemSourceIcon';
import { xeroArchiveConfirmMessage, xeroArchiveStepNeeded, xeroItemRestoreWords } from '../utils/xeroArchiveNotice';
import FormulationButton from './FormulationButton';
import { CustomerLinkBadges } from './PricingBadges';
import usePersistedState from '../hooks/usePersistedState';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import LoadingSpinner from './LoadingSpinner';
import useColumnFilters from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import ExportTableButton from './ExportTableButton';
import { useNavStore } from '../store/navStore';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { useReveal, revealFilters } from '../utils/revealInSearch';

const MINERAL_ACCOUNT_CODE = '206';

/**
 * Columns filterable from their header. Keys must match the whitelist in the
 * inventory search route; an unknown key narrows nothing.
 */
const COLUMN_FILTERS = {
  item_name: { label: 'Item name' },
  description: { label: 'Description' },
  product_group: { label: 'Product group' },
  supplier: { label: 'Supplier' },
  status: { label: 'Status' }
};

/*
 * Sales and purchase account used to be header filters too. They are chosen in
 * the filter row now (Cam, 2026-10-16), so a selection left in this tab's
 * stored column filters from before is dropped on arrival — otherwise it would
 * go on narrowing the search with no header to show it or take it off.
 */
const RETIRED_COLUMN_FILTERS = ['sales_account_code', 'purchase_account_code'];

/** An account filter as a list of codes. */
const accountList = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean).map(String);
  return v ? [String(v)] : [];
};

/** The account's name, from the chart of accounts, else our own list. */
const accountName = (a) => a.name || ACCOUNT_NAMES[a.code] || '';

/** Options for an account filter: code + name, searchable by either. */
const accountOptions = (accounts) => accounts.map(a => ({
  value: a.code,
  label: (
    <span className="acc-code">
      <span className="code">{a.code}</span>
      {accountName(a)}
    </span>
  ),
  searchText: `${a.code} ${accountName(a)}`
}));

/** What the closed account filter says: all, the one chosen, or how many. */
const summarizeAccounts = (accounts) => (value) => {
  if (!value || value.length === 0) return 'All Accounts';
  if (value.length === 1) {
    const a = accounts.find(x => x.code === value[0]);
    const name = a ? accountName(a) : '';
    return name ? `${value[0]} ${name}` : value[0];
  }
  return `${value.length} accounts`;
};

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight and the toolbar pill (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  search: '', isSold: '', isPurchased: '', isTracked: '',
  salesAccounts: [], purchaseAccounts: [], productGroups: [], suppliers: [], activeOnly: true, source: '',
  page: 1, limit: 20
};

/** A product group filter as a list of ids (strings) and/or 'none'. */
const groupList = (v) => (Array.isArray(v) ? v.filter(Boolean).map(String) : []);

/** The Supplier cell / export's words for a search row ("—" for none). */
const supplierCellText = (item) => supplierText(item) || '—';

function SearchInventory({ user, onNavigate }) {
  const [items, setItems] = useState([]);
  // Calculated costs for the rows on screen, keyed by item id. Fetched after the
  // search rather than joined into it: costing a formulation is a multi-table
  // aggregate, and putting it in /inventory/search would slow every search for
  // everyone, including the majority of items that have no formulation.
  const [calcCosts, setCalcCosts] = useState({});

  /**
   * Cost the formulated items on screen, so a cost price that disagrees with its
   * own recipe can be flagged in the table.
   *
   * Only 202/207A (blends) are asked for: costing needs a formulation bridged to
   * priced materials, and everything else would be a guaranteed miss.
   */
  useEffect(() => {
    const ids = (items || [])
      .filter((i) => ['202', '207A'].includes(String(i.sales_account_code || '').toUpperCase()))
      .map((i) => i.id)
      .filter(Boolean);
    if (!ids.length) { setCalcCosts({}); return undefined; }

    let cancelled = false;
    fetch(`${API_URL}/api/blends/formulation-cost?itemIds=${ids.join(',')}`,
      { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setCalcCosts(d.costs || {}); })
      // Silent: a user without blend_costs gets a 403, and no highlight is the
      // right outcome rather than an error on the inventory screen.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [items]);

  /**
   * Is this row's cost price far enough from its recipe to be worth flagging?
   *
   * Coverage has to be high before flagging: a figure covering 60% of a recipe is
   * a known understatement, so flagging it would cry wolf on data we already know
   * is incomplete. Above 95% the gap is real.
   */
  const costDiscrepancy = useCallback((item) => {
    const calc = calcCosts[item.id] || calcCosts[String(item.id)];
    if (!calc || !calc.cost || calc.coverage_pct < 95) return null;
    const current = item.purchase_unit_price === null || item.purchase_unit_price === undefined
      ? null : Number(item.purchase_unit_price);
    if (!current) return null;
    const diffPct = ((calc.cost - current) / current) * 100;
    if (Math.abs(diffPct) <= 5) return null;
    return { calc: calc.cost, current, diffPct, coverage: calc.coverage_pct };
  }, [calcCosts]);
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);
  // The live product groups for the filter (N8): null until read. `ready`
  // false before migration product_groups.sql (or for a rep).
  const [groupOptions, setGroupOptions] = useState(null);
  // The suppliers (and groups) items name, for the Supplier filter — not
  // ready before migration item_supplier.sql.
  const supplierLists = useItemSupplierOptions();
  const [loading, setLoading] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit'
  // The add/edit form, its validation, the Xero conflict check and the save
  // request all live in InventoryItemFormModal now — this screen only decides
  // which item to open and what to do once one is saved.
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [showSyncWarning, setShowSyncWarning] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [costHistory, setCostHistory] = useState({ points: [], current_cost: null });
  // Distinguishes "nothing recorded" from "not fetched yet" — without it the
  // empty-state note flashes up before the request has even landed.
  const [costHistoryLoaded, setCostHistoryLoaded] = useState(false);
  // Minerals (Sales Account Code 206) open the Mineral Pricing modal instead of the generic
  // inventory modal, so cost/code/description + haulage/margin/notes are edited in one place.
  const [mineralModal, setMineralModal] = useState({ open: false, mineral: null, mode: 'view' });

  useSlideOutButton([showModal, selectedItem]);

  // Drag-to-resize columns (12 cols: item code, name, description, product
  // group, supplier, purchase price, sales price, sales account, purchase
  // account, qty, status, actions). Widths are stored by column position, so
  // the key moves with each new column (Product group N8, Supplier 2 Nov) — a
  // width dragged before would land on the wrong column.
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('searchInventoryCols12', 12);

  // Arriving from a modal's "Find in …" (utils/revealInSearch.js): the term
  // seeds the filters and the page goes back to 1 in the FIRST render's state,
  // so the mount search is the one that was asked for.
  const reveal = useReveal('search-inventory');
  const [pagination, setPagination] = usePersistedState('searchInventory.pagination', {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  }, { seed: reveal && { page: 1 } });

  const [filters, setFilters] = usePersistedState('searchInventory.filters', {
    search: '',
    isSold: '',
    isPurchased: '',
    isTracked: '',
    salesAccounts: [],     // account codes; none = every account
    purchaseAccounts: [],
    productGroups: [],     // live groups' ids and/or 'none' (No group); none chosen = every item
    suppliers: [],         // 'a:<id>' suppliers, 'g:<parent id>' groups and/or 'none'; none chosen = every item
    activeOnly: true,  // Filter out archived items by default
    source: '',        // '', 'xero', 'local' or 'local_only' — see ITEM_SOURCES
    page: 1,
    limit: 20
  }, {
    // Layered over the defaults so a state stored before the account filters
    // became lists still has them (an old single-code `salesAccount` /
    // `purchaseAccount` left in it is ignored).
    merge: true,
    seed: revealFilters(reveal)
  });
  const salesAccountSel = accountList(filters.salesAccounts);
  const purchaseAccountSel = accountList(filters.purchaseAccounts);
  const groupSel = groupList(filters.productGroups);
  const supplierSel = groupList(filters.suppliers);
  // Whether the search answered with product groups (the column, sort and
  // filters mean something) — from the search itself, so a rep or a server
  // before the migration shows "—" and greys the filter.
  const [groupsState, setGroupsState] = useState({ ready: true });
  // Likewise the Supplier column (migration item_supplier.sql).
  const [suppliersState, setSuppliersState] = useState({ ready: true });

  const [sortField, setSortField] = usePersistedState('searchInventory.sortField', 'item_code');
  const [sortDirection, setSortDirection] = usePersistedState('searchInventory.sortDirection', 'asc');
  const isInitialMount = useRef(true);
  // Distinct values per filterable column across the whole result set.
  const [facets, setFacets] = useState({});

  // Right-click menu. No row selection on this page, so no bulk group.
  // Right-click a header to narrow by the values on this page.
  const cols = useColumnFilters(items, COLUMN_FILTERS, {
    facets,
    storageKey: 'searchInventory.columnFilters',
    onChange: () => setPagination(prev => ({ ...prev, page: 1 })),
    // the Product group column's blank is an item in no group; the
    // Supplier column's an item with no supplier
    optionLabel: (key, value) => (key === 'product_group' ? groupColumnLabel(value)
      : key === 'supplier' ? supplierColumnLabel(value) : value)
  });

  // Drop a retired header filter left in this tab's store (see
  // RETIRED_COLUMN_FILTERS). One key per pass: each removal re-renders with
  // the new selection, and the next pass takes the other.
  useEffect(() => {
    const stale = RETIRED_COLUMN_FILTERS.find(k => Object.prototype.hasOwnProperty.call(cols.selected, k));
    if (stale) cols.selectAll(stale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.selected]);

  /**
   * The item list on paper.
   *
   * No detail block: this table has no expanded view, and an item has no lines
   * to show. Inventing a sub-table here would print something the screen does
   * not have.
   */
  const buildExport = () => {
    const criteria = [];
    if (filters.search) criteria.push({ label: 'Search', value: filters.search });
    if (salesAccountSel.length) {
      criteria.push({ label: 'Sales account', value: salesAccountSel.join(', ') });
    }
    if (purchaseAccountSel.length) {
      criteria.push({ label: 'Purchase account', value: purchaseAccountSel.join(', ') });
    }
    if (groupSel.length) {
      criteria.push({ label: 'Product group', value: summarizeGroupFilter(groupOptions)(groupSel) });
    }
    if (supplierSel.length) {
      criteria.push({ label: 'Supplier', value: summarizeSupplierFilter(supplierLists)(supplierSel) });
    }
    criteria.push({ label: 'Items', value: filters.activeOnly ? 'Active only' : 'Active and archived' });
    if (filters.source) {
      criteria.push({ label: 'Source', value: ITEM_SOURCES[filters.source]?.label || filters.source });
    }
    for (const [key, values] of Object.entries(cols.selected)) {
      if (!Array.isArray(values)) continue;
      criteria.push({ label: cols.definitions[key]?.label || key, value: values.join(', ') || 'none' });
    }

    return {
      title: 'Inventory Items',
      kicker: 'INVENTORY',
      rowNoun: cols.rows.length === 1 ? 'item' : 'items',
      filename: `inventory-${new Date().toISOString().slice(0, 10)}`,
      landscape: true,
      columns: [
        { key: 'code', label: 'Item #', width: '9%' },
        { key: 'name', label: 'Item Name', width: '18%' },
        { key: 'description', label: 'Description', width: '16%' },
        { key: 'group', label: 'Product group', width: '12%' },
        { key: 'supplier', label: 'Supplier', width: '12%' },
        { key: 'cost', label: 'Cost', width: '7%', numeric: true },
        { key: 'price', label: 'Price', width: '7%', numeric: true },
        { key: 'salesAcc', label: 'Sales Acc', width: '6%' },
        { key: 'purchaseAcc', label: 'Purchase', width: '7%' },
        { key: 'stock', label: 'Stock', width: '7%', numeric: true }
      ],
      rows: cols.rows.map((item) => ({
        code: item.item_code || '',
        name: item.item_name || '',
        description: item.description || '',
        group: groupCellText(item),
        supplier: supplierCellText(item),
        cost: item.purchase_unit_price !== null && item.purchase_unit_price !== undefined
          ? formatPrice(item.purchase_unit_price) : '',
        price: item.sales_unit_price !== null && item.sales_unit_price !== undefined
          ? formatPrice(item.sales_unit_price) : '',
        salesAcc: item.sales_account_code || '',
        purchaseAcc: item.purchase_account_code || '',
        // Gated on is_tracked_as_inventory, exactly as the table is. Only 60 of
        // ~4,000 items are tracked and the rest hold a literal 0, so printing
        // the figure would put "0.00" against thousands of items that
        // simply do not carry stock.
        stock: item.is_tracked_as_inventory
          ? formatQuantity(item.quantity_on_hand || 0)
          : '—'
      })),
      totals: [{ code: `${cols.rows.length} items` }],
      criteria
    };
  };

  const ctxMenu = useTableContextMenu({
    rows: items,
    getRowId: (item) => item.id
  });

  useEffect(() => {
    fetchAccountCodes();
    fetchGroupOptions();
    // Back to the page you were on, not page 1: the filters are restored from
    // the nav store, so the page number should be too.
    searchItems(pagination.page || 1);
    // Mark initial mount as complete after a short delay to let state settle
    setTimeout(() => {
      isInitialMount.current = false;
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search on filter change (skip during initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    const timeoutId = setTimeout(() => {
      searchItems(1);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // A column filter is part of the query, so changing one re-runs the search
  // the same way changing a filter above the table does.
  useEffect(() => {
    if (isInitialMount.current) return;
    searchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols.queryValue]);

  const fetchAccountCodes = async () => {
    try {
      const [salesRes, purchaseRes] = await Promise.all([
        fetch(`${API_URL}/api/inventory/sales-accounts`, {
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/inventory/purchase-accounts`, {
          credentials: 'include'
        })
      ]);

      const salesData = await salesRes.json();
      const purchaseData = await purchaseRes.json();

      // The endpoints now return [{ code, name }] from the cached Xero chart of
      // accounts rather than bare codes already in use by an item. Tolerate the
      // old shape so a stale server during a rolling deploy doesn't blank these.
      const normalise = (list) => (list || []).map(a => (typeof a === 'string' ? { code: a, name: null } : a));
      setSalesAccounts(normalise(salesData.accounts));
      setPurchaseAccounts(normalise(purchaseData.accounts));
    } catch (error) {
      console.error('Failed to fetch account codes:', error);
    }
  };

  // The live product groups for the filter row (N8). A failure leaves the
  // filter greyed with the reason rather than offering nothing silently.
  const fetchGroupOptions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/inventory/product-group-options`, { credentials: 'include' });
      const data = await res.json();
      setGroupOptions(res.ok ? data : { ready: false, reason: data.error || 'Product groups could not be read', groups: [] });
    } catch (error) {
      setGroupOptions({ ready: false, reason: 'Product groups could not be read', groups: [] });
    }
  };

  // Typing fires a search per pause and the responses can land out of order —
  // only the newest may write the table.
  const latest = useLatestRequest();

  const searchItems = async (page = 1) => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        search: filters.search || '',
        isSold: filters.isSold || '',
        isPurchased: filters.isPurchased || '',
        isTracked: filters.isTracked || '',
        // Several codes each, comma-separated (an account code has no comma).
        salesAccounts: salesAccountSel.join(','),
        purchaseAccounts: purchaseAccountSel.join(','),
        // live groups' ids and/or 'none' (No group)
        productGroups: groupSel.join(','),
        // suppliers, supplier groups and/or 'none' (No supplier)
        suppliers: supplierSel.join(','),
        activeOnly: filters.activeOnly ? 'true' : 'false',
        source: filters.source || '',
        page: page,
        limit: filters.limit || 50,
        sortField: sortField,
        sortDirection: sortDirection
      });

      // Column-header filters travel with the search, so they narrow the whole
      // result set and survive paginating and re-searching.
      if (cols.queryValue) queryParams.set('columnFilters', cols.queryValue);
      const response = await fetch(`${API_URL}/api/inventory/search?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search

      if (data.success) {
        setItems(data.items || []);
        if (data.facets) setFacets(data.facets);
        if (data.product_groups) setGroupsState(data.product_groups);
        if (data.item_suppliers) setSuppliersState(data.item_suppliers);
        setPagination(data.pagination || {
          page: page,
          limit: parseInt(filters.limit),
          total: 0,
          totalPages: 0
        });
      }
    } catch (error) {
      if (!latest.isLatest(seq)) return;
      console.error('Search failed:', error);
      setAlertDialog({ isOpen: true, message: 'Failed to search inventory: ' + error.message, type: 'error' });
    }
    if (latest.isLatest(seq)) setLoading(false);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    if (isInitialMount.current) return;
    searchItems(pagination.page || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      searchItems(newPage);
    }
  };

  const syncInventory = async () => {
    setLoading(true);
    setSyncProgress(0);
    setSyncMessage('Initiating sync...');

    try {
      const eventSource = new EventSource(
        `${API_URL}/api/sync/inventory`,
        { withCredentials: true }
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          const progress = data.percentage != null
            ? data.percentage
            : data.totalPages > 0
              ? Math.min(95, Math.round((data.currentPage / data.totalPages) * 100))
              : Math.min(95, Math.round((data.currentPage * 100) / Math.max(data.currentPage + 1, 10)));
          setSyncProgress(progress);
          setSyncMessage(data.message || `Fetching page ${data.currentPage}... (${data.recordsFetched} items)`);
        } else if (data.type === 'complete') {
          setSyncProgress(100);
          setSyncMessage('Sync complete!');

          const result = data.result;
          // Archive changes are the ones a user is least likely to expect, so
          // name them rather than folding them into the totals.
          const parts = [`${result.added} new`, `${result.updated} updated`];
          if (result.archived > 0) parts.push(`${result.archived} archived (gone from Xero)`);
          setAlertDialog({ isOpen: true, message: `Synced ${result.count} items: ${parts.join(', ')}`, type: 'success' });
          searchItems(1);
          eventSource.close();

          setTimeout(() => {
            setLoading(false);
            setSyncProgress(0);
            setSyncMessage('');
          }, 1000);
        } else if (data.type === 'error') {
          setAlertDialog({ isOpen: true, message: `Sync failed: ${data.error}`, type: 'error' });
          eventSource.close();
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
        }
      };

      eventSource.onerror = () => {
        setAlertDialog({ isOpen: true, message: 'Connection to server lost during sync', type: 'error' });
        eventSource.close();
        setLoading(false);
        setSyncProgress(0);
        setSyncMessage('');
      };
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to sync: ${error.message}`, type: 'error' });
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
    }
  };

  // Deep-link into the Mineral Pricing page, pre-filtered to the given item code.
  const goToMineralPricing = (code) => {
    useNavStore.getState().setPage('mineralPricing.filters', { search: code || '', limit: '20', activeOnly: true });
    setAlertDialog({ isOpen: false, message: '', type: 'info' });
    if (onNavigate) onNavigate('mineral-pricing');
  };

  // Open the Mineral Pricing modal for a 206 item; returns false if it has no mineral row yet
  // (falls back to the normal inventory modal).
  const openMineralForItem = async (item, mode) => {
    if (item?.sales_account_code !== MINERAL_ACCOUNT_CODE || !item?.id) return false;
    try {
      const res = await fetch(`${API_URL}/api/pricing/minerals/by-item/${item.id}`, { credentials: 'include' });
      if (!res.ok) return false;
      const mineral = await res.json();
      setMineralModal({ open: true, mineral, mode });
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleViewItem = async (item) => {
    if (await openMineralForItem(item, 'view')) return;
    setSelectedItem(item);
    setModalMode('view');
    setShowModal(true);
    setCostHistory({ points: [], current_cost: null });
    setCostHistoryLoaded(false);
    if (item?.id) {
      fetch(`${API_URL}/api/inventory/${item.id}/cost-prices`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => setCostHistory({ points: d.points || [], current_cost: d.current_cost ?? null, source: d.source || null, till: d.till || null }))
        .catch(() => {})
        .finally(() => setCostHistoryLoaded(true));
    }
  };

  const handleEditItem = async (item) => {
    if (await openMineralForItem(item, 'edit')) return;
    setSelectedItem(item);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || 20 }));
    setSortField('item_code');
    setSortDirection('asc');
    // Column filters are filters; leaving them on after a button labelled
    // "clear all filters" is how a search gets reported as broken.
    cols.clearEverything();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, {
    labels: {
      isSold: 'Sold', isPurchased: 'Purchased', isTracked: 'Tracked', activeOnly: 'Active only',
      salesAccounts: 'Sales account', purchaseAccounts: 'Purchase account', productGroups: 'Product group',
      suppliers: 'Supplier'
    }
  });
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;

  const handleAddItem = () => {
    setSelectedItem(null);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteItem = async (item) => {
    setConfirmDialog({
      isOpen: true,
      message: `Are you sure you want to delete "${item.item_code} - ${item.item_name}"?`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await fetch(`${API_URL}/api/inventory/${item.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });

          const data = await response.json();

          if (response.ok && data.success) {
            setAlertDialog({ isOpen: true, message: 'Item deleted successfully', type: 'success' });
            searchItems(pagination.page);
          } else {
            setAlertDialog({ isOpen: true, message: `Failed to delete item: ${data.error || 'Unknown error'}`, type: 'error' });
          }
        } catch (error) {
          console.error('Delete error:', error);
          setAlertDialog({ isOpen: true, message: `Failed to delete item: ${error.message}`, type: 'error' });
        }
      }
    });
  };

  const handleArchiveItem = async (item) => {
    const newArchivedStatus = !item.is_archived;
    const action = newArchivedStatus ? 'archive' : 'restore';
    // Restoring here never restores it in Xero (its Item API can't change
    // archived state): say so, with Xero's address as text to copy — not a
    // link (Cam, 2 Nov 2026). See utils/xeroArchiveNotice.
    const restore = newArchivedStatus ? null : xeroItemRestoreWords(item.xero_item_id);

    setConfirmDialog({
      isOpen: true,
      title: newArchivedStatus ? 'Archive this item?' : 'Restore this item?',
      message: restore
        ? (
          <div className="inventory-restore-note">
            <p>Restore “{item.item_code} - {item.item_name}” here?</p>
            <p>{restore.before}</p>
            {restore.url && <p className="inventory-restore-url"><code>{restore.url}</code></p>}
          </div>
        )
        // Until the cutover, archiving also has to be done in Xero — the Item
        // API cannot be told about archived state at all.
        : xeroArchiveConfirmMessage(action, `${item.item_code} - ${item.item_name}`, 'item'),
      confirmText: newArchivedStatus ? 'Archive' : 'Restore',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await fetch(`${API_URL}/api/inventory/${item.id}/archive`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ is_archived: newArchivedStatus })
          });

          const data = await response.json();

          if (response.ok && data.success) {
            setAlertDialog({
              isOpen: true,
              message: `Item ${newArchivedStatus ? 'archived' : 'restored'} successfully.`
                + (restore && restore.after
                    ? ` ${restore.after}`
                    : (xeroArchiveStepNeeded() ? ` Remember to ${action} it in Xero as well.` : '')),
              type: 'success'
            });
            searchItems(pagination.page);
          } else {
            setAlertDialog({
              isOpen: true,
              message: `Failed to ${action} item: ${data.error || 'Unknown error'}`,
              type: 'error'
            });
          }
        } catch (error) {
          console.error('Archive error:', error);
          setAlertDialog({
            isOpen: true,
            message: `Failed to ${action} item: ${error.message}`,
            type: 'error'
          });
        }
      }
    });
  };

  // A group's name opens it on the Product Groups tab (the item window's link
  // does the same): its id is handed over, then the tab is switched to.
  const openGroup = onNavigate ? (groupId) => {
    useNavStore.getState().setPage(OPEN_GROUP_KEY, groupId);
    onNavigate('search-inventory', { tab: 'product-groups' });
  } : null;

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setModalMode('view');
    // The row underneath may have just been edited. onSaved already refreshes
    // on an explicit save; this covers the rest — a price changed through one
    // of the modal's own actions, or a save that reported itself another way.
    searchItems(pagination.page);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Shared by the row actions menu and the right-click context menu so the two
  // surfaces can never drift apart.
  /* "Where else does this code appear?" — the question people currently answer
     by copying the code, navigating, and pasting it. readSafe because it only
     ever reads: it seeds another screen's search box and goes there. */
  const [crossSearchItem, setCrossSearchItem] = useState(null);

  const handleCrossSearch = (target) => {
    const item = crossSearchItem;
    setCrossSearchItem(null);
    if (!item) return;
    // Close the item modal on the way out — navigating with it still open
    // leaves it sitting over the results the user asked to see.
    if (showModal) closeModal();
    runCrossSearch(target, item.item_code, onNavigate);
  };

  const buildRowActions = (item) => [
    { key: 'view', icon: 'visibility', label: 'View', title: 'View Details', onClick: () => handleViewItem(item) },
    {
      key: 'cross-search',
      icon: 'search',
      label: 'Search for this item\u2026',
      readSafe: true,
      title: 'Find this item in orders, invoices or purchase orders',
      onClick: () => setCrossSearchItem(item)
    },
    { key: 'edit', icon: 'edit', label: 'Edit', disabled: item.is_archived, title: item.is_archived ? 'Cannot edit — item is archived' : 'Edit Item', onClick: () => { if (!item.is_archived) handleEditItem(item); } },
    item.is_archived
      ? { key: 'archive', icon: 'unarchive', label: 'Restore', title: 'Restore Item (here only — it stays archived in Xero)', onClick: () => handleArchiveItem(item) }
      : { key: 'archive', icon: 'archive', label: 'Archive', title: 'Archive Item', onClick: () => handleArchiveItem(item) },
    { key: 'delete', icon: 'delete', label: 'Delete', disabled: item.is_archived, className: item.is_archived ? undefined : 'action-danger', title: item.is_archived ? 'Cannot delete — item is archived' : 'Delete Item', onClick: () => { if (!item.is_archived) handleDeleteItem(item); } },
  ];

  return (
    <div className="search-inventory">
      <CrossSearchDialog
        isOpen={!!crossSearchItem}
        itemCode={crossSearchItem?.item_code}
        itemName={crossSearchItem?.item_name}
        onChoose={handleCrossSearch}
        onCancel={() => setCrossSearchItem(null)}
      />
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Search, add, and manage inventory items.</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              Edits save straight to Xero. Archiving is local only — Xero can't
              report it, so it stays as you set it here.
              <LastXeroSync entity="inventory_items" />
            </span>
          </span>
        </div>
        <div className='search-header-inner' style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <ExportTableButton
                build={buildExport}
                user={user}
                rowCount={cols.rows.length}
                onError={(message) => setAlertDialog({ isOpen: true, message, type: 'error' })}
              />
            </div>
            <div>
              <button
                onClick={handleClearFilters}
                className={clearFiltersClass(filtersApplied)}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            {user?.is_admin && (
              <div>
                <button onClick={() => setShowSyncWarning(true)} className="btn-slideout admin-item" disabled={loading}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">sync</span>
                  </div>
                  <span className="btn-label">Sync from Xero</span>
                </button>
              </div>
            )}
            <div>
              <button onClick={handleAddItem} className="btn-slideout" disabled={loading}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp
                fields={['item code', 'name', 'description', 'purchase description', 'sales account', 'purchase account',
                  'product group', 'variant / additive', 'supplier (name or account number; a supplier group\'s depots too)']}
              />
            </label>
            <Input
              type="text"
              placeholder="Item code, name, or description..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onEnter={() => searchItems(1)}
              icon="search"
            />
          </div>

          <div className="filter-group">
            <label>Sales Account</label>
            <MultiSelectDropdown
              options={accountOptions(salesAccounts)}
              value={salesAccountSel}
              onChange={(value) => handleFilterChange('salesAccounts', value)}
              searchable
              searchPlaceholder="Search by code or name…"
              emptyMessage="No accounts match"
              tickBoxes
              hideTags
              summarize={summarizeAccounts(salesAccounts)}
            />
          </div>

          <div className="filter-group">
            <label>Purchase Account</label>
            <MultiSelectDropdown
              options={accountOptions(purchaseAccounts)}
              value={purchaseAccountSel}
              onChange={(value) => handleFilterChange('purchaseAccounts', value)}
              searchable
              searchPlaceholder="Search by code or name…"
              emptyMessage="No accounts match"
              tickBoxes
              hideTags
              summarize={summarizeAccounts(purchaseAccounts)}
            />
          </div>

          <div className="filter-group" title={groupFilterBlock(groupOptions) || undefined}>
            <label>Product Group</label>
            {/* Live groups and "No group" (N8). Greyed with the reason before
                the migration, while the list is read, or with no groups yet. */}
            <MultiSelectDropdown
              options={groupFilterOptions(groupOptions)}
              value={groupSel}
              onChange={(value) => handleFilterChange('productGroups', value)}
              searchable
              searchPlaceholder="Search groups…"
              emptyMessage="No groups match"
              tickBoxes
              hideTags
              disabled={!!groupFilterBlock(groupOptions)}
              summarize={summarizeGroupFilter(groupOptions)}
            />
          </div>

          <div className="filter-group" title={supplierFilterBlock(supplierLists) || undefined}>
            <label>Supplier</label>
            {/* No supplier, each supplier group, each supplier an item names.
                Greyed with the reason before the migration or while read. */}
            <MultiSelectDropdown
              options={supplierFilterOptions(supplierLists)}
              value={supplierSel}
              onChange={(value) => handleFilterChange('suppliers', value)}
              searchable
              searchPlaceholder="Search suppliers…"
              emptyMessage="No suppliers match"
              tickBoxes
              hideTags
              disabled={!!supplierFilterBlock(supplierLists)}
              summarize={summarizeSupplierFilter(supplierLists)}
            />
          </div>

          <div className="filter-group">
            <label>Source</label>
            <Dropdown
              value={filters.source}
              onChange={(value) => handleFilterChange('source', value)}
              options={[
                { value: '', label: 'All Sources' },
                { value: 'xero', label: ITEM_SOURCES.xero.label },
                { value: 'local', label: ITEM_SOURCES.local.label },
                { value: 'local_only', label: ITEM_SOURCES.local_only.label }
              ]}
              placeholder="All Sources"
            />
          </div>

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="20"
            />
          </div>

          <div className="filter-group">
            <label>Active Only</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="active-only"
                  type="checkbox"
                  checked={filters.activeOnly}
                  onChange={(e) => handleFilterChange('activeOnly', e.target.checked)}
                />
                <label htmlFor="active-only"></label>
              </div>
            </div>
          </div>

        </div>
      </div>

      {syncProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}

      <div className="table-toolbar">
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          <ResetButton />
          <ColumnFilterNote cols={cols} header={headerFilters} onClear={handleClearFilters} />
        </div>
        {pagination && (
          <PaginationControls
            pagination={pagination}
            onPageChange={changePage}
          />
        )}
      </div>

      <div className="inventory-table-container">
        <div className='data-table'>
          <table className="data-table inventory-table resizable-table table-min-lg" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th onClick={() => handleSort('item_code')} className="sortable">
                  Item # {sortField === 'item_code' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={0} />
                </th>
                <th onClick={() => handleSort('item_name')}
                    onContextMenu={cols.headerProps('item_name').onContextMenu}
                    className={`sortable ${cols.headerProps('item_name').className || ''}`.trim()}>
                  Item Name {sortField === 'item_name' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={1} />
                </th>
                <th onContextMenu={cols.headerProps('description').onContextMenu}
                    className={cols.headerProps('description').className}>
                  Description<ResizeHandle colIndex={2} />
                </th>
                {/* Product group (N8): sorts by the group's name (items in no
                    group last either way); right-click to filter by group. */}
                <th onClick={() => handleSort('product_group')}
                    onContextMenu={cols.headerProps('product_group').onContextMenu}
                    className={`sortable group-col ${cols.headerProps('product_group').className || ''}`.trim()}
                    title={groupsState.ready
                      ? 'Sort by product group — right-click to filter by group'
                      : (groupsState.reason || 'Product groups are not ready')}>
                  Product Group {sortField === 'product_group' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                {/* Supplier (2 Nov 2026): sorts by name (items with none last
                    either way); right-click to filter by supplier. */}
                <th onClick={() => handleSort('supplier')}
                    onContextMenu={cols.headerProps('supplier').onContextMenu}
                    className={`sortable supplier-col ${cols.headerProps('supplier').className || ''}`.trim()}
                    title={suppliersState.ready
                      ? 'Sort by supplier — right-click to filter by supplier'
                      : (suppliersState.reason || 'Item suppliers are not ready')}>
                  Supplier {sortField === 'supplier' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                <th onClick={() => handleSort('purchase_unit_price')} className="sortable" style={{ textAlign: 'center' }}>
                  Cost {sortField === 'purchase_unit_price' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={5} />
                </th>
                <th onClick={() => handleSort('sales_unit_price')} className="sortable" style={{ textAlign: 'center' }}>
                  Price {sortField === 'sales_unit_price' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={6} />
                </th>
                <th onClick={() => handleSort('sales_account_code')} className="sortable"
                    style={{ textAlign: 'center' }}
                    title="Filter by sales account in the filters above">
                  Sales Acc {sortField === 'sales_account_code' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={7} />
                </th>
                <th onClick={() => handleSort('purchase_account_code')} className="sortable"
                    style={{ textAlign: 'center' }}
                    title="Filter by purchase account in the filters above">
                  Purchase {sortField === 'purchase_account_code' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={8} />
                </th>
                <th onClick={() => handleSort('quantity_on_hand')} className="sortable" style={{ textAlign: 'center' }} title="Stock on hand (tracked items only — synced nightly from Xero)">
                  Stock {sortField === 'quantity_on_hand' && (sortDirection === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={9} />
                </th>
                <th style={{ textAlign: 'center' }}
                    onContextMenu={cols.headerProps('status').onContextMenu}
                    className={`status-col ${cols.headerProps('status').className || ''}`.trim()}
                    title="Right-click to filter by status on this page">
                  Status<ResizeHandle colIndex={10} />
                </th>
                <th className="actions-col"><ResizeHandle colIndex={11} /></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="loading-cell">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : cols.rows.length === 0 ? (
                <tr>
                  <td colSpan="12" className="empty-cell">No inventory items found</td>
                </tr>
              ) : (
                cols.rows.map(item => (
                  /* Archived rows are dimmed with the shared .row-archived —
                     the same treatment Search Customer Orders and Search
                     Haulage Jobs give theirs, including the hover rule that
                     stops a dimmed row reading as disabled. They appear only
                     when Active Only is off. */
                  <tr key={item.id}
                      className={item.is_archived ? 'row-archived' : undefined}
                      onContextMenu={ctxMenu.onRowContextMenu(item)}>
                    <td onClick={() => handleViewItem(item)}>
                      <span className="item-code">{item.item_code}</span>
                    </td>
                    {/* Badges say this product is priced for a named customer,
                        and how — the thing that used to be legible only from a
                        farm name typed into the item name. */}
                    <td className="item-name" onClick={() => handleViewItem(item)}>
                      {item.item_name}
                      <CustomerLinkBadges links={item.customer_links} />
                    </td>
                    <td className="description" onClick={() => handleViewItem(item)}>{item.description || '-'}</td>
                    {/* The group's name opens it on the Product Groups tab; the
                        variant / additive under it; an archived group faded. */}
                    <td className={`group-col${item.product_group_archived ? ' group-archived' : ''}`}
                        onClick={() => handleViewItem(item)}>
                      {groupsState.ready && item.product_group_name ? (
                        <div className="group-cell">
                          {openGroup ? (
                            <button type="button" className="group-link"
                                    onClick={(e) => { e.stopPropagation(); openGroup(item.product_group_id); }}
                                    title={item.product_group_archived
                                      ? `${item.product_group_name} — an archived group. Open it on the Product Groups tab`
                                      : `Open ${item.product_group_name} on the Product Groups tab`}>
                              {item.product_group_name}
                            </button>
                          ) : <span className="group-name">{item.product_group_name}</span>}
                          <span className="group-variant">
                            {item.product_group_archived ? 'Archived group · ' : ''}
                            {variantLabel(item.product_group_additive)}
                          </span>
                        </div>
                      ) : <span className="group-none" title={groupsState.ready ? 'In no product group' : (groupsState.reason || undefined)}>—</span>}
                    </td>
                    {/* Who it is bought from: a supplier, or a supplier group
                        (any depot — one is picked on each order). */}
                    <td className={`supplier-col${item.supplier_archived ? ' supplier-archived' : ''}`}
                        onClick={() => handleViewItem(item)}>
                      {suppliersState.ready && item.supplier_name ? (
                        <div className="supplier-cell" title={item.supplier_group_parent_id
                          ? `${supplierCellText(item)} — any depot, picked on each order`
                          : `${supplierCellText(item)}${item.supplier_archived ? ' — an archived contact' : ''}`}>
                          <span className="supplier-name">{supplierCellText(item)}</span>
                          {(item.supplier_group_parent_id || item.supplier_archived) && (
                            <span className="supplier-sub">
                              {item.supplier_group_parent_id ? 'Any depot' : 'Archived contact'}
                            </span>
                          )}
                        </div>
                      ) : <span className="supplier-none" title={suppliersState.ready ? 'No supplier' : (suppliersState.reason || undefined)}>—</span>}
                    </td>
                    <td style={{ textAlign: 'center' }} className="numeric" onClick={() => handleViewItem(item)}>
                      {(() => {
                        // Two independent reasons a cost price wants attention:
                        // it's stale, or it disagrees with its own recipe. Both
                        // get the same red treatment, and the title states which
                        // — showing only one would hide the other, and an item
                        // can easily be both.
                        const gap = costDiscrepancy(item);
                        if (!item.cost_stale && !gap) return formatPrice(item.purchase_unit_price);
                        const reasons = [];
                        if (item.cost_stale) {
                          reasons.push(
                            `Cost last updated ${item.cost_updated_at ? formatDate(item.cost_updated_at) : 'unknown'}`
                            + ' — not refreshed for the current pricing period');
                        }
                        if (gap) {
                          reasons.push(
                            `Recipe costs ${formatPrice(gap.calc)} `
                            + `(${gap.diffPct > 0 ? '+' : ''}${gap.diffPct.toFixed(1)}%`
                            + `, covering ${gap.coverage}% of the ingredients)`);
                        }
                        return (
                          <span className="cost-stale-value" title={reasons.join('. ')}>
                            {formatPrice(item.purchase_unit_price)}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }} className="numeric" onClick={() => handleViewItem(item)}>{formatPrice(item.sales_unit_price)}</td>
                    <td className="account-code" onClick={() => handleViewItem(item)}><div className='code'>{item.sales_account_code || '-'}</div></td>
                    {/* A tracked item has no purchase account in Xero — it posts to
                        its inventory account, with COGS on sale (utils/itemAccounts).
                        Each code carries its kind; an untracked item shows its
                        purchase account plain. */}
                    <td className="account-code" onClick={() => handleViewItem(item)}
                        title={purchaseSideText(item) || undefined}>
                      <div className='code'>
                        {purchaseSideAccounts(item).length ? purchaseSideAccounts(item).map(a => (
                          <span key={a.kind} className="purchase-side-code">
                            {a.code}
                            {a.short && <span className="cost-acct-kind">{a.short}</span>}
                          </span>
                        )) : '-'}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }} className="numeric stock-col" onClick={() => handleViewItem(item)} title={item.is_tracked_as_inventory ? `Cost pool: ${formatCurrency(item.total_cost_pool || 0)}` : 'Not tracked as inventory'}>
                      {item.is_tracked_as_inventory ? formatQuantity(item.quantity_on_hand || 0) : '—'}
                    </td>
                    <td className="status-col" onClick={() => handleViewItem(item)}>
                      <span style={{ textAlign: 'center' }} className={`status-badge ${item.is_archived ? 'archived' : 'active'}`}>
                        {item.is_archived ? 'Archived' : 'Active'}
                      </span>
                      <ItemSourceIcon item={item} />
                    </td>
                    <td className="actions-col">
                      <RowActionsMenu actions={buildRowActions(item)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && modalMode === 'view' && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content item-modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="header-left">
                <h3>Item Details</h3>
              </div>
              <div className="header-actions">
                {selectedItem && (
                  <button
                    className="btn-icon"
                    onClick={() => setShowHistory(true)}
                    title="View History"
                  >
                    <span className="material-symbols-rounded">history</span>
                  </button>
                )}
                <button className="btn-icon close-btn" onClick={closeModal} title="Close">
                  <span className="material-symbols-rounded">close</span>
                </button>
              </div>
            </div>
            <div className="modal-body">
              {selectedItem && (
                <div className="item-details">
                  <div className="detail-row code-name">
                    <div className="detail-group">
                      <label>Item Code</label>
                      <div className="detail-value">{selectedItem.item_code}</div>
                    </div>
                    <div className="detail-group">
                      <label>Item Name</label>
                      <div className="detail-value">{selectedItem.item_name}</div>
                    </div>
                  </div>
                  {/* Both of them, each named for its side — Xero's
                      `Description` (quotes and invoices) and
                      `PurchaseDescription` (purchase orders and bills). Kept in
                      step with the same block in InventoryItemModal, which is
                      this layout's other copy. */}
                  <div className="detail-row description">
                    <div className="detail-group">
                      <label>Sales Description</label>
                      <div className="detail-value">{selectedItem.description || '-'}</div>
                    </div>
                  </div>
                  <div className="detail-row description">
                    <div className="detail-group">
                      <label>Purchase Description</label>
                      <div className="detail-value">{selectedItem.purchase_description || '-'}</div>
                    </div>
                  </div>
                  {/* What a quantity of this item actually counts — bags, totes
                      or tonnes. Above the prices on purpose: a unit price means
                      one thing per bag and another per tonne. */}
                  <ItemUnitsRow item={selectedItem} />
                  <ProductGroupItemRow
                    itemId={selectedItem.id}
                    onOpenGroup={openGroup ? (groupId) => {
                      // the Product Groups tab opens this group's window
                      closeModal();
                      openGroup(groupId);
                    } : null}
                  />
                  {/* Who it is bought from — Change supplier… here too */}
                  <ItemSupplierRow itemId={selectedItem.id} />
                  {selectedItem.is_sold && (
                    <div className="detail-row sales">
                      <div className="detail-group">
                        <label>Sales Price</label>
                        <div className="detail-value">{formatPrice(selectedItem.sales_unit_price)}</div>
                      </div>
                      <div className="detail-group">
                        <label>Sales Account</label>
                        <div className="detail-value">
                          {selectedItem.sales_account_code ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                              <span className="account-badge">{selectedItem.sales_account_code}</span>
                              {ACCOUNT_NAMES[selectedItem.sales_account_code] || ''}
                            </span>
                          ) : '-'}
                        </div>
                      </div>
                      <div className="detail-group">
                        <label>Sales Tax Type</label>
                        <div className="detail-value">{getTaxTypeLabel(selectedItem.sales_tax_type)}</div>
                      </div>
                    </div>
                  )}
                  {selectedItem.is_purchased && (
                    <div className="detail-row purchases">
                      <div className="detail-group">
                        <label>Purchase Price</label>
                        <div className="detail-value">{formatPrice(selectedItem.purchase_unit_price)}</div>
                      </div>
                      {/* Tracked: inventory account + COGS; untracked: purchase account (utils/itemAccounts). */}
                      <div className="detail-group">
                        <label>{selectedItem.is_tracked_as_inventory ? 'Inventory / COGS Accounts' : 'Purchase Account'}</label>
                        <div className="detail-value purchase-side-accounts">
                          {purchaseSideAccounts(selectedItem).length ? purchaseSideAccounts(selectedItem).map(a => (
                            <span key={a.kind} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                              {a.kind !== 'purchase' && <span className="cost-acct-kind">{a.short}</span>}
                              <span className="account-badge">{a.code}</span>
                              {ACCOUNT_NAMES[a.code] || ''}
                            </span>
                          )) : '-'}
                        </div>
                      </div>
                      <div className="detail-group">
                        <label>Purchase Tax Type</label>
                        <div className="detail-value">{getTaxTypeLabel(selectedItem.purchase_tax_type)}</div>
                      </div>
                    </div>
                  )}
                  {selectedItem.is_tracked_as_inventory && (() => {
                    const qoh = parseFloat(selectedItem.quantity_on_hand || 0);
                    const pool = parseFloat(selectedItem.total_cost_pool || 0);
                    const avgCost = qoh > 0 ? pool / qoh : null;
                    return (
                      <div style={{ margin: "var(--spacing-sm) 0" }}>
                        <h4>Stock Control</h4>
                        <div className="detail-row stock">
                          <div className="detail-group">
                            <label>Quantity on Hand</label>
                            <div className="detail-value">{formatQuantity(qoh)}</div>
                          </div>
                          <div className="detail-group">
                            <label>Total Cost Pool</label>
                            <div className="detail-value">{formatCurrency(pool)}</div>
                          </div>
                          <div className="detail-group">
                            <label>Avg Cost / Unit</label>
                            <div className="detail-value">{avgCost != null ? formatPrice(avgCost) : '—'}</div>
                          </div>
                          <div className="detail-group">
                            <label>Stock Last Synced</label>
                            <div className="detail-value">
                              {selectedItem.synced_at
                                ? new Date(selectedItem.synced_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
                                : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Normal flow, not inside the section below — see the note
                      in InventoryItemModal.js: that section is gated on cost
                      history, which would hide this on exactly the items that
                      need it. */}
                  <CalculatedCostPanel
                    itemId={selectedItem.id}
                    currentCost={selectedItem.purchase_unit_price}
                  />

                  {/* Last in the flow, below everything else, scrolling with
                      the rest of the body — styled from SearchInventory.css
                      like every other part of this modal.

                      It was pinned here with position:absolute; bottom:77px and
                      its own background, which is three separate problems in
                      one declaration: out of the flow it added nothing to the
                      modal's height, so the box sized itself as though the
                      chart were not there; it did not scroll with the content,
                      because there was nothing to scroll it with; and it drew
                      its own panel in the same colour as the footer directly
                      below it, so the two read as one shape. The 77px was the
                      footer's height, written down. */}
                  {/* Two points make a history; one makes a dot. The chart used
                      to render for a single price, which drew an axis, a legend
                      and one marker to say something a sentence says better —
                      and an item with none at all showed nothing, leaving no way
                      to tell "no cost recorded" from "the request failed". Both
                      cases say so now. */}
                  {costHistoryLoaded && (
                    <CostPriceHistorySection history={costHistory} />
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
                <>
                  {/* Left of the footer, and only when this item has one. The
                      .footer-right group below flips the footer to
                      space-between so it sits opposite Edit. */}
                  <FormulationButton itemId={selectedItem?.id} />
                  {/* Same action as the row's 3-dot menu, offered where you
                      already are — having opened the item to look at it is
                      exactly when the question "where else does this appear?"
                      comes up. */}
                  {selectedItem?.item_code && (
                    <button
                      data-read-safe
                      className="btn-slideout"
                      onClick={() => setCrossSearchItem(selectedItem)}
                      title="Find this item in orders, invoices or purchase orders"
                    >
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">search</span>
                      </div>
                      <span className="btn-label">Search for this item</span>
                    </button>
                  )}
                  <div className="footer-right">
                    <button onClick={() => handleEditItem(selectedItem)} className="btn-slideout">
                      <div className="icon-wrapper">
                        <span className="material-symbols-rounded">edit</span>
                      </div>
                      <span className="btn-label">Edit</span>
                    </button>
                  </div>
                </>
            </div>
          </div>
        </div>,
        document.body
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        confirmText={confirmDialog.confirmText}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {showHistory && selectedItem && (
        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType="inventory_item"
          entityId={selectedItem.id}
          entityIdentifier={`${selectedItem.item_code} - ${selectedItem.item_name}`}
        />
      )}

      <ConfirmDialog
        isOpen={showSyncWarning}
        title="Sync Inventory from Xero"
        message="This will overwrite all local inventory data with the latest from Xero. Any local changes not yet pushed to Xero will be lost."
        type="warning"
        confirmText="Sync"
        onConfirm={() => { setShowSyncWarning(false); syncInventory(); }}
        onCancel={() => setShowSyncWarning(false)}
      />

      {/* Add and edit both run through the shared form modal, which owns the
          form, its validation and the Xero conflict check. This screen only says
          which item, and what to do once one comes back. */}
      <InventoryItemFormModal
        isOpen={showModal && modalMode === 'edit'}
        item={selectedItem}
        onClose={closeModal}
        onSaved={(saved, { isEditing, updatedInXero }) => {
          let message = `Item ${isEditing ? 'updated' : 'created'} successfully`;
          if (updatedInXero) message += ' (synced to Xero)';
          // A new mineral needs haulage and margin setting before it prices
          // correctly, and Mineral Pricing is the only place to do it.
          if (!isEditing && saved?.sales_account_code === MINERAL_ACCOUNT_CODE) {
            const code = saved.item_code;
            message = (
              <div>
                <p>Mineral product created{updatedInXero ? ' (synced to Xero)' : ''}.</p>
                <p>
                  <a
                    href="#mineral-pricing"
                    onClick={(e) => { e.preventDefault(); goToMineralPricing(code); }}
                    style={{ color: 'var(--color-primary)', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Review its pricing in Mineral Pricing →
                  </a>
                </p>
              </div>
            );
          }
          setAlertDialog({ isOpen: true, message, type: 'success' });
          searchItems(pagination.page);
        }}
      />

      {/* Table context menu (right-click) */}
      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          {
            key: 'add',
            icon: 'add',
            label: 'Add item',
            disabled: loading,
            onClick: handleAddItem
          },
          {
            key: 'refresh',
            icon: 'refresh',
            label: 'Refresh results',
            disabled: loading,
            onClick: () => searchItems(pagination.page || 1)
          },
          {
            key: 'show-archived',
            icon: 'inventory_2',
            label: 'Show archived items',
            active: !filters.activeOnly,
            onClick: () => handleFilterChange('activeOnly', !filters.activeOnly)
          },
          {
            key: 'clear-filters',
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: handleClearFilters
          }
        ]}
      />

      {mineralModal.open && (
        <MineralPricingModal
          mineral={mineralModal.mineral}
          mode={mineralModal.mode}
          onClose={() => { setMineralModal({ open: false, mineral: null, mode: 'view' }); searchItems(pagination.page); }}
          onSave={() => { setMineralModal({ open: false, mineral: null, mode: 'view' }); searchItems(pagination.page); }}
        />
      )}
      <ColumnFilterMenu cols={cols} />
    </div>
  );
}

export default SearchInventory;
